import { useState } from 'react';
import { Badge, Box, Button, Divider, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import { useSettings } from '../../lib/settingsStore';
import { useNotifications } from './useNotifications';
import { markRead, useReadIds } from './readStore';

/** The bell in the top bar: how many notifications are unread, and a short list to open them from */
export function NotificationBell() {
  useSettings(); // re-draws when a kind of notification is switched on or off
  const navigate = useNavigate();
  const read = useReadIds();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const all = useNotifications();
  const unread = all.filter((n) => !read.includes(n.id));
  // the ones that need you come first
  const shown = [...all]
    .sort((a, b) => Number(b.group === 'Needs Attention') - Number(a.group === 'Needs Attention'))
    .slice(0, 6);
  const go = (to: string, ids: string[]) => {
    markRead(ids);
    setAnchor(null);
    void navigate(to);
  };

  return (
    <>
      <IconButton
        aria-label={`Notifications, ${unread.length} unread`}
        aria-haspopup="menu"
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <Badge badgeContent={unread.length} color="error">
          <NotificationsNoneOutlined />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '92vw' } } }}
      >
        <Stack direction="row" sx={{ px: 2, py: 1, alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unread.length > 0 && (
            <Button size="small" onClick={() => markRead(all.map((n) => n.id))}>
              Mark all as read
            </Button>
          )}
        </Stack>
        <Divider />
        {shown.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            You are all caught up.
          </Typography>
        )}
        {shown.map((n) => (
          <MenuItem
            key={n.id}
            onClick={() => go(n.action.to, [n.id])}
            sx={{ whiteSpace: 'normal', alignItems: 'flex-start', opacity: read.includes(n.id) ? 0.65 : 1 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                mt: 0.9,
                mr: 1.25,
                flexShrink: 0,
                bgcolor: read.includes(n.id) ? 'transparent' : 'primary.main',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 650 }}>
                {n.srb ? `${n.srb}: ` : ''}
                {n.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {n.category} · {n.when}
              </Typography>
            </Box>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => go('/notifications', [])}
          sx={{ justifyContent: 'center', color: 'primary.main', fontWeight: 600 }}
        >
          View all notifications
        </MenuItem>
      </Menu>
    </>
  );
}
